import React, { useMemo, useState, useEffect } from 'react';
import { 
    Play, 
    Check, 
    X, 
    Search, 
    Filter, 
    Loader2, 
    Calendar, 
    BarChart3, 
    Users, 
    Clock, 
    TrendingUp, 
    Eye, 
    DollarSign,
    Activity,
    ArrowLeft,
    PlayCircle,
    StopCircle
} from 'lucide-react';
import apiService from '../services/api';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import LiveStreamAnalyticsCharts from '../components/charts/LiveStreamAnalyticsCharts';

const LiveStreams = () => {
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterStatus, setFilterStatus] = useState('PENDING'); // PENDING, APPROVED, ALL
    const [searchQuery, setSearchQuery] = useState('');
    const [streamStatusFilter, setStreamStatusFilter] = useState('ALL'); // ALL, SCHEDULED, LIVE, ENDED, etc.
    const [accessTypeFilter, setAccessTypeFilter] = useState('ALL'); // ALL, FREE, PAID, etc.
    const [languageFilter, setLanguageFilter] = useState('ALL');
    const [scheduleFrom, setScheduleFrom] = useState(''); // yyyy-mm-dd
    const [scheduleTo, setScheduleTo] = useState(''); // yyyy-mm-dd
    const [sortOrder, setSortOrder] = useState('SCHEDULE_ASC'); // SCHEDULE_ASC | SCHEDULE_DESC
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [showAnalytics, setShowAnalytics] = useState(false);
    const [analyticsData, setAnalyticsData] = useState(null);
    const [analyticsLoading, setAnalyticsLoading] = useState(false);
    const [liveStreamStats, setLiveStreamStats] = useState(null);
    const [statsLoading, setStatsLoading] = useState(true);
    const [pagination, setPagination] = useState(null);

    const activeFilterChips = useMemo(() => {
        const chips = [];

        if (filterStatus !== 'ALL') chips.push({ key: 'approval', label: `Approval: ${filterStatus}` });
        if (streamStatusFilter !== 'ALL') chips.push({ key: 'stream', label: `Status: ${streamStatusFilter}` });
        if (accessTypeFilter !== 'ALL') chips.push({ key: 'access', label: `Access: ${accessTypeFilter}` });
        if (languageFilter !== 'ALL') chips.push({ key: 'lang', label: `Lang: ${languageFilter}` });
        if (scheduleFrom) chips.push({ key: 'from', label: `From: ${scheduleFrom}` });
        if (scheduleTo) chips.push({ key: 'to', label: `To: ${scheduleTo}` });
        if (sortOrder !== 'SCHEDULE_ASC') chips.push({ key: 'sort', label: `Sort: ${sortOrder === 'SCHEDULE_DESC' ? 'Newest' : 'Upcoming'}` });
        if (searchQuery.trim()) chips.push({ key: 'q', label: `Search: ${searchQuery.trim()}` });

        return chips;
    }, [filterStatus, streamStatusFilter, accessTypeFilter, languageFilter, scheduleFrom, scheduleTo, sortOrder, searchQuery]);

    const fetchEvents = async (opts = {}) => {
        try {
            setLoading(true);
            const params = { page: 1, limit: 50, sort: sortOrder, ...opts };

            if (filterStatus !== 'ALL') params.approvalStatus = filterStatus;
            if (streamStatusFilter !== 'ALL') params.streamStatus = streamStatusFilter;
            if (accessTypeFilter !== 'ALL') params.accessType = accessTypeFilter;
            if (languageFilter !== 'ALL') params.language = languageFilter;
            if (scheduleFrom) params.scheduleFrom = `${scheduleFrom}T00:00:00.000Z`;
            if (scheduleTo) params.scheduleTo = `${scheduleTo}T23:59:59.999Z`;
            if (searchQuery.trim()) params.q = searchQuery.trim();

            const response = await apiService.getLiveEvents(params);
            if (response && response.data) {
                setEvents(response.data);
                setPagination(response.pagination || null);
            } else {
                setEvents([]);
                setPagination(null);
            }
        } catch (error) {
            toast.error('Failed to fetch events');
            console.error(error);
            setEvents([]);
            setPagination(null);
        } finally {
            setLoading(false);
        }
    };

    const fetchLiveStreamStats = async () => {
        try {
            setStatsLoading(true);
            const response = await apiService.getLiveStreamStats();
            if (response && response.data) {
                setLiveStreamStats(response.data);
            } else {
                setLiveStreamStats(null);
            }
        } catch (error) {
            console.error('Failed to fetch live stream stats:', error);
            toast.error('Failed to load statistics');
            setLiveStreamStats(null);
        } finally {
            setStatsLoading(false);
        }
    };

    const fetchEventAnalytics = async (eventId) => {
        try {
            setAnalyticsLoading(true);
            const response = await apiService.getLiveEventAnalytics(eventId);
            if (response && response.data) {
                setAnalyticsData(response.data);
            } else {
                setAnalyticsData(null);
            }
        } catch (error) {
            toast.error('Failed to fetch event analytics');
            console.error(error);
            setAnalyticsData(null);
        } finally {
            setAnalyticsLoading(false);
        }
    };

    useEffect(() => {
        const timer = setTimeout(() => {
            fetchEvents();
        }, 350);
        return () => clearTimeout(timer);
    }, [
        filterStatus,
        searchQuery,
        streamStatusFilter,
        accessTypeFilter,
        languageFilter,
        scheduleFrom,
        scheduleTo,
        sortOrder,
    ]);

    useEffect(() => {
        fetchLiveStreamStats();
    }, []);

    const handleStatusUpdate = async (id, status) => {
        try {
            await apiService.updateLiveEventStatus(id, { approvalStatus: status });
            toast.success(`Event ${status.toLowerCase()} successfully`);
            fetchEvents();
            fetchLiveStreamStats();
        } catch (error) {
            toast.error('Failed to update status');
            console.error(error);
        }
    };

    const handleViewAnalytics = (event) => {
        setSelectedEvent(event);
        setShowAnalytics(true);
        fetchEventAnalytics(event.id);
    };

    const closeAnalytics = () => {
        setShowAnalytics(false);
        setSelectedEvent(null);
        setAnalyticsData(null);
    };

    // Calculate stats from events if API stats are not available
    const displayStats = liveStreamStats || {
        totalEvents: events.length,
        pendingApprovals: events.filter(e => e.approvalStatus === 'PENDING').length,
        approvedEvents: events.filter(e => e.approvalStatus === 'APPROVED').length,
        liveEvents: events.filter(e => e.streamStatus === 'LIVE').length,
        endedEvents: events.filter(e => e.streamStatus === 'ENDED').length,
        totalViewers: events.reduce((sum, e) => sum + (e.peakViewerCount || 0), 0),
        totalRevenue: 0 // Real revenue data would come from backend
    };

    const clearFilters = () => {
        setSearchQuery('');
        setStreamStatusFilter('ALL');
        setAccessTypeFilter('ALL');
        setLanguageFilter('ALL');
        setScheduleFrom('');
        setScheduleTo('');
        setSortOrder('SCHEDULE_ASC');
    };

    return (
        <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Live Stream Management</h1>
                    <p className="mt-1 text-sm text-gray-500">Manage live streams and view performance analytics</p>
                </div>
                <div className="flex gap-2 items-center">
                    <button
                        onClick={fetchEvents}
                        className="inline-flex items-center gap-2 px-3 py-2 border rounded-md hover:bg-gray-50 text-sm"
                        title="Refresh"
                    >
                        <Filter className="w-4 h-4 text-gray-600" />
                        Refresh
                    </button>
                </div>
            </div>

            {/* Filters */}
            <div className="bg-white rounded-lg shadow-sm border">
                <div className="px-4 py-3 border-b flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex items-center gap-2">
                        <div className="p-2 bg-orange-50 rounded-md">
                            <Filter className="w-4 h-4 text-orange-600" />
                        </div>
                        <div>
                            <div className="text-sm font-semibold text-gray-900">Filters</div>
                            <div className="text-xs text-gray-500">Refine results</div>
                        </div>
                    </div>

                    <div className="flex items-center gap-2">
                        <button
                            onClick={clearFilters}
                            className="inline-flex items-center gap-2 px-3 py-2 border rounded-md hover:bg-gray-50 text-sm"
                            title="Reset filters"
                        >
                            Reset
                        </button>
                        <button
                            onClick={() => fetchEvents()}
                            className="inline-flex items-center gap-2 px-3 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 text-sm"
                            title="Apply / Refresh"
                        >
                            <Loader2 className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                            Apply
                        </button>
                    </div>
                </div>

                <div className="p-4">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-3">
                        <div className="lg:col-span-5">
                            <label className="block text-xs font-medium text-gray-600 mb-1">Search</label>
                            <div className="relative">
                                <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                                <input
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    placeholder="Title, host, city, category..."
                                    className="w-full pl-9 pr-3 py-2.5 border border-gray-400 bg-white rounded-md text-sm shadow-sm focus:ring-2 focus:ring-orange-500/30 focus:border-orange-500"
                                />
                            </div>
                        </div>

                        <div className="lg:col-span-2">
                            <label className="block text-xs font-medium text-gray-600 mb-1">Approval</label>
                            <select
                                value={filterStatus}
                                onChange={(e) => setFilterStatus(e.target.value)}
                                className="w-full border border-gray-400 bg-white rounded-md shadow-sm text-sm py-2.5 focus:ring-2 focus:ring-orange-500/30 focus:border-orange-500"
                            >
                                <option value="PENDING">Pending</option>
                                <option value="APPROVED">Approved</option>
                                <option value="ALL">All</option>
                            </select>
                        </div>

                        <div className="lg:col-span-2">
                            <label className="block text-xs font-medium text-gray-600 mb-1">Stream Status</label>
                            <select
                                value={streamStatusFilter}
                                onChange={(e) => setStreamStatusFilter(e.target.value)}
                                className="w-full border border-gray-400 bg-white rounded-md shadow-sm text-sm py-2.5 focus:ring-2 focus:ring-orange-500/30 focus:border-orange-500"
                            >
                                <option value="ALL">All</option>
                                <option value="SCHEDULED">Scheduled</option>
                                <option value="LIVE">Live</option>
                                <option value="ENDED">Ended</option>
                            </select>
                        </div>

                        <div className="lg:col-span-3 grid grid-cols-1 sm:grid-cols-3 gap-3">
                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Access</label>
                                <select
                                    value={accessTypeFilter}
                                    onChange={(e) => setAccessTypeFilter(e.target.value)}
                                    className="w-full border border-gray-400 bg-white rounded-md shadow-sm text-sm py-2.5 focus:ring-2 focus:ring-orange-500/30 focus:border-orange-500"
                                >
                                    <option value="ALL">All</option>
                                    <option value="FREE">Free</option>
                                    <option value="PAID">Paid</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Language</label>
                                <select
                                    value={languageFilter}
                                    onChange={(e) => setLanguageFilter(e.target.value)}
                                    className="w-full border border-gray-400 bg-white rounded-md shadow-sm text-sm py-2.5 focus:ring-2 focus:ring-orange-500/30 focus:border-orange-500"
                                >
                                    <option value="ALL">All</option>
                                    <option value="Hindi">Hindi</option>
                                    <option value="English">English</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Sort</label>
                                <select
                                    value={sortOrder}
                                    onChange={(e) => setSortOrder(e.target.value)}
                                    className="w-full border border-gray-400 bg-white rounded-md shadow-sm text-sm py-2.5 focus:ring-2 focus:ring-orange-500/30 focus:border-orange-500"
                                >
                                    <option value="SCHEDULE_ASC">Upcoming</option>
                                    <option value="SCHEDULE_DESC">Newest</option>
                                </select>
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-12 gap-3 mt-3">
                        <div className="md:col-span-3">
                            <label className="block text-xs font-medium text-gray-600 mb-1">From</label>
                            <input
                                type="date"
                                value={scheduleFrom}
                                onChange={(e) => setScheduleFrom(e.target.value)}
                                className="w-full border border-gray-400 bg-white rounded-md shadow-sm text-sm py-2.5 focus:ring-2 focus:ring-orange-500/30 focus:border-orange-500"
                            />
                        </div>
                        <div className="md:col-span-3">
                            <label className="block text-xs font-medium text-gray-600 mb-1">To</label>
                            <input
                                type="date"
                                value={scheduleTo}
                                onChange={(e) => setScheduleTo(e.target.value)}
                                className="w-full border border-gray-400 bg-white rounded-md shadow-sm text-sm py-2.5 focus:ring-2 focus:ring-orange-500/30 focus:border-orange-500"
                            />
                        </div>

                        <div className="md:col-span-6 flex items-end justify-between gap-3">
                            <div className="text-sm text-gray-600">
                                Results: <span className="font-semibold text-gray-900">{events.length}</span>
                                {pagination?.total !== undefined ? (
                                    <>
                                        {' '}of <span className="font-semibold text-gray-900">{pagination.total}</span>
                                    </>
                                ) : null}
                            </div>
                        </div>
                    </div>

                    {activeFilterChips.length > 0 ? (
                        <div className="mt-3 flex flex-wrap gap-2">
                            {activeFilterChips.map((chip) => (
                                <span
                                    key={chip.key}
                                    className="inline-flex items-center px-2.5 py-1 rounded-full text-xs border bg-gray-50 text-gray-700"
                                >
                                    {chip.label}
                                </span>
                            ))}
                        </div>
                    ) : (
                        <div className="mt-3 text-xs text-gray-500">No filters applied.</div>
                    )}
                </div>
            </div>

            {/* Live Stream Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="bg-white p-6 rounded-lg shadow-sm border">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-gray-600">Total Events</p>
                            <p className="text-2xl font-bold text-gray-900">
                                {statsLoading ? '...' : displayStats.totalEvents}
                            </p>
                        </div>
                        <div className="p-3 bg-blue-100 rounded-full">
                            <Calendar className="w-6 h-6 text-blue-600" />
                        </div>
                    </div>
                </div>

                <div className="bg-white p-6 rounded-lg shadow-sm border">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-gray-600">Live Now</p>
                            <p className="text-2xl font-bold text-red-600">
                                {statsLoading ? '...' : displayStats.liveEvents}
                            </p>
                        </div>
                        <div className="p-3 bg-red-100 rounded-full">
                            <PlayCircle className="w-6 h-6 text-red-600" />
                        </div>
                    </div>
                </div>

                <div className="bg-white p-6 rounded-lg shadow-sm border">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-gray-600">Total Viewers</p>
                            <p className="text-2xl font-bold text-green-600">
                                {statsLoading ? '...' : displayStats.totalViewers?.toLocaleString() || '0'}
                            </p>
                        </div>
                        <div className="p-3 bg-green-100 rounded-full">
                            <Users className="w-6 h-6 text-green-600" />
                        </div>
                    </div>
                </div>

                <div className="bg-white p-6 rounded-lg shadow-sm border">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-gray-600">Revenue</p>
                            <p className="text-2xl font-bold text-purple-600">
                                {statsLoading ? '...' : `₹${(displayStats.totalRevenue || 0).toLocaleString()}`}
                            </p>
                        </div>
                        <div className="p-3 bg-purple-100 rounded-full">
                            <DollarSign className="w-6 h-6 text-purple-600" />
                        </div>
                    </div>
                </div>
            </div>

            <div className="bg-white shadow rounded-lg overflow-hidden">
                {loading ? (
                    <div className="p-12 flex justify-center">
                        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
                    </div>
                ) : events.length === 0 ? (
                    <div className="p-12 text-center text-gray-500">No events found.</div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Host</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Event Details</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Schedule</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Performance</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {events.map((event) => (
                                    <tr key={event.id}>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center">
                                                <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                                                    {event.host?.profile?.image ? (
                                                        <img src={event.host.profile.image} alt="" className="h-10 w-10 rounded-full" />
                                                    ) : (
                                                        <span className="text-gray-500 font-bold">{event.host?.profile?.name?.[0]}</span>
                                                    )}
                                                </div>
                                                <div className="ml-4">
                                                    <div className="text-sm font-medium text-gray-900">{event.host?.profile?.name || 'Unknown Host'}</div>
                                                    <div className="text-sm text-gray-500">{event.host?.profile?.city}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="text-sm font-medium text-gray-900">{event.title}</div>
                                            <div className="text-sm text-gray-500">{event.category} • {event.language}</div>
                                            <div className="text-sm text-gray-500">{event.accessType} {event.price && `• ₹${event.price}`}</div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm text-gray-900 flex items-center">
                                                <Calendar className="w-4 h-4 mr-1 text-gray-400" />
                                                {format(new Date(event.scheduleTime), 'MMM dd, h:mm a')}
                                            </div>
                                            <div className="text-xs text-gray-500 mt-1">{event.duration} mins</div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm text-gray-900 flex items-center space-x-2">
                                                <div className="flex items-center">
                                                    <Users className="w-4 h-4 mr-1 text-blue-500" />
                                                    <span>{event.peakViewerCount || 0}</span>
                                                </div>
                                                {event.streamStatus === 'LIVE' && (
                                                    <div className="flex items-center">
                                                        <Activity className="w-4 h-4 mr-1 text-red-500" />
                                                        <span className="text-red-600 font-medium">LIVE</span>
                                                    </div>
                                                )}
                                            </div>
                                            {event.streamStatus === 'ENDED' && event.startedAt && event.endedAt && (
                                                <div className="text-xs text-gray-500 mt-1">
                                                    Duration: {Math.round((new Date(event.endedAt) - new Date(event.startedAt)) / (1000 * 60))} mins
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
                        ${event.approvalStatus === 'APPROVED' ? 'bg-green-100 text-green-800' :
                                                    event.approvalStatus === 'REJECTED' ? 'bg-red-100 text-red-800' :
                                                        'bg-yellow-100 text-yellow-800'}`}>
                                                {event.approvalStatus}
                                            </span>
                                            <div className="mt-1">
                                                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
                          ${event.streamStatus === 'LIVE' ? 'bg-red-100 text-red-800' :
                                                        event.streamStatus === 'ENDED' ? 'bg-gray-100 text-gray-800' :
                                                            event.streamStatus === 'SCHEDULED' ? 'bg-blue-100 text-blue-800' :
                                                                'bg-orange-100 text-orange-800'}`}>
                                                    {event.streamStatus}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                            <div className="flex justify-end gap-2">
                                                {/* <button
                                                    onClick={() => handleViewAnalytics(event)}
                                                    className="text-blue-600 hover:text-blue-900 bg-blue-50 p-2 rounded-full"
                                                    title="View Analytics"
                                                >
                                                    <BarChart3 className="w-5 h-5" />
                                                </button> */}
                                                {event.approvalStatus === 'PENDING' && (
                                                    <>
                                                        <button
                                                            onClick={() => handleStatusUpdate(event.id, 'APPROVED')}
                                                            className="text-green-600 hover:text-green-900 bg-green-50 p-2 rounded-full"
                                                            title="Approve"
                                                        >
                                                            <Check className="w-5 h-5" />
                                                        </button>
                                                        <button
                                                            onClick={() => handleStatusUpdate(event.id, 'REJECTED')}
                                                            className="text-red-600 hover:text-red-900 bg-red-50 p-2 rounded-full"
                                                            title="Reject"
                                                        >
                                                            <X className="w-5 h-5" />
                                                        </button>
                                                    </>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Analytics Modal */}
            {showAnalytics && (
                <AnalyticsModal 
                    event={selectedEvent}
                    analyticsData={analyticsData}
                    loading={analyticsLoading}
                    onClose={closeAnalytics}
                />
            )}
        </div>
    );
};

// Analytics Modal Component
const AnalyticsModal = ({ event, analyticsData, loading, onClose }) => {
    if (!event) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] overflow-y-auto">
                {/* Modal Header */}
                <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
                    <div>
                        <h2 className="text-xl font-bold text-gray-900">Event Performance Analytics</h2>
                        <p className="text-sm text-gray-500">{event.title}</p>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                    >
                        <X className="w-5 h-5 text-gray-500" />
                    </button>
                </div>

                {/* Modal Content */}
                <div className="p-6">
                    {loading ? (
                        <div className="flex justify-center py-12">
                            <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
                        </div>
                    ) : analyticsData ? (
                        <div className="space-y-8">
                            {/* Event Overview */}
                            <div className="bg-gray-50 rounded-lg p-6">
                                <h3 className="text-lg font-semibold text-gray-900 mb-4">Event Overview</h3>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div className="bg-white p-4 rounded-lg">
                                        <div className="text-2xl font-bold text-blue-600">{event.streamStatus}</div>
                                        <div className="text-sm text-gray-500">Stream Status</div>
                                    </div>
                                    <div className="bg-white p-4 rounded-lg">
                                        <div className="text-2xl font-bold text-green-600">
                                            {format(new Date(event.scheduleTime), 'MMM dd, HH:mm')}
                                        </div>
                                        <div className="text-sm text-gray-500">Scheduled Time</div>
                                    </div>
                                    <div className="bg-white p-4 rounded-lg">
                                        <div className="text-2xl font-bold text-purple-600">{event.duration} mins</div>
                                        <div className="text-sm text-gray-500">Planned Duration</div>
                                    </div>
                                </div>
                            </div>

                            {/* Viewer Metrics */}
                            <div>
                                <h3 className="text-lg font-semibold text-gray-900 mb-4">Viewer Metrics</h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                                    <div className="bg-white p-6 rounded-lg shadow-sm border">
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <p className="text-sm font-medium text-gray-600">Total Viewers</p>
                                                <p className="text-2xl font-bold text-blue-600">
                                                    {analyticsData.viewerMetrics.totalViewers.toLocaleString()}
                                                </p>
                                            </div>
                                            <Users className="w-8 h-8 text-blue-500" />
                                        </div>
                                    </div>
                                    
                                    <div className="bg-white p-6 rounded-lg shadow-sm border">
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <p className="text-sm font-medium text-gray-600">Peak Viewers</p>
                                                <p className="text-2xl font-bold text-green-600">
                                                    {analyticsData.viewerMetrics.peakViewers.toLocaleString()}
                                                </p>
                                            </div>
                                            <TrendingUp className="w-8 h-8 text-green-500" />
                                        </div>
                                    </div>
                                    
                                    <div className="bg-white p-6 rounded-lg shadow-sm border">
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <p className="text-sm font-medium text-gray-600">Current Viewers</p>
                                                <p className="text-2xl font-bold text-orange-600">
                                                    {analyticsData.viewerMetrics.currentViewers.toLocaleString()}
                                                </p>
                                            </div>
                                            <Activity className="w-8 h-8 text-orange-500" />
                                        </div>
                                    </div>
                                    
                                    <div className="bg-white p-6 rounded-lg shadow-sm border">
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <p className="text-sm font-medium text-gray-600">Avg. Watch Time</p>
                                                <p className="text-2xl font-bold text-purple-600">
                                                    {analyticsData.viewerMetrics.averageWatchTime}
                                                </p>
                                            </div>
                                            <Clock className="w-8 h-8 text-purple-500" />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Financial Metrics */}
                            {event.accessType === 'PAID' && (
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Financial Performance</h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                                        <div className="bg-white p-6 rounded-lg shadow-sm border">
                                            <div className="flex items-center justify-between">
                                                <div>
                                                    <p className="text-sm font-medium text-gray-600">Total Revenue</p>
                                                    <p className="text-2xl font-bold text-green-600">
                                                        ₹{analyticsData.financialMetrics.totalRevenue.toLocaleString()}
                                                    </p>
                                                </div>
                                                <DollarSign className="w-8 h-8 text-green-500" />
                                            </div>
                                        </div>
                                        
                                        <div className="bg-white p-6 rounded-lg shadow-sm border">
                                            <div className="flex items-center justify-between">
                                                <div>
                                                    <p className="text-sm font-medium text-gray-600">Total Payments</p>
                                                    <p className="text-2xl font-bold text-blue-600">
                                                        {analyticsData.financialMetrics.totalPayments}
                                                    </p>
                                                </div>
                                                <Users className="w-8 h-8 text-blue-500" />
                                            </div>
                                        </div>
                                        
                                        <div className="bg-white p-6 rounded-lg shadow-sm border">
                                            <div className="flex items-center justify-between">
                                                <div>
                                                    <p className="text-sm font-medium text-gray-600">Avg. Ticket Price</p>
                                                    <p className="text-2xl font-bold text-purple-600">
                                                        ₹{analyticsData.financialMetrics.averageTicketPrice}
                                                    </p>
                                                </div>
                                                <BarChart3 className="w-8 h-8 text-purple-500" />
                                            </div>
                                        </div>
                                        
                                        <div className="bg-white p-6 rounded-lg shadow-sm border">
                                            <div className="flex items-center justify-between">
                                                <div>
                                                    <p className="text-sm font-medium text-gray-600">Conversion Rate</p>
                                                    <p className="text-2xl font-bold text-orange-600">
                                                        {analyticsData.financialMetrics.conversionRate}
                                                    </p>
                                                </div>
                                                <TrendingUp className="w-8 h-8 text-orange-500" />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Engagement Metrics */}
                            <div>
                                <h3 className="text-lg font-semibold text-gray-900 mb-4">Engagement Analytics</h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                                    <div className="bg-white p-6 rounded-lg shadow-sm border">
                                        <p className="text-sm font-medium text-gray-600">Join Rate</p>
                                        <p className="text-2xl font-bold text-green-600">{analyticsData.engagement.joinRate}</p>
                                        <p className="text-xs text-gray-500 mt-1">Viewers who joined</p>
                                    </div>
                                    <div className="bg-white p-6 rounded-lg shadow-sm border">
                                        <p className="text-sm font-medium text-gray-600">Drop-off Rate</p>
                                        <p className="text-2xl font-bold text-red-600">{analyticsData.engagement.dropOffRate}</p>
                                        <p className="text-xs text-gray-500 mt-1">Early exits</p>
                                    </div>
                                    <div className="bg-white p-6 rounded-lg shadow-sm border">
                                        <p className="text-sm font-medium text-gray-600">Session Duration</p>
                                        <p className="text-2xl font-bold text-blue-600">{analyticsData.engagement.averageSessionDuration}</p>
                                        <p className="text-xs text-gray-500 mt-1">Average time spent</p>
                                    </div>
                                    <div className="bg-white p-6 rounded-lg shadow-sm border">
                                        <p className="text-sm font-medium text-gray-600">Returning Viewers</p>
                                        <p className="text-2xl font-bold text-purple-600">{analyticsData.engagement.returningViewers}</p>
                                        <p className="text-xs text-gray-500 mt-1">Repeat attendees</p>
                                    </div>
                                </div>
                            </div>

                            {/* Technical Performance */}
                            <div>
                                <h3 className="text-lg font-semibold text-gray-900 mb-4">Technical Performance</h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                                    <div className="bg-white p-6 rounded-lg shadow-sm border">
                                        <p className="text-sm font-medium text-gray-600">Stream Quality</p>
                                        <p className="text-lg font-bold text-green-600">{analyticsData.technical.streamQuality}</p>
                                    </div>
                                    <div className="bg-white p-6 rounded-lg shadow-sm border">
                                        <p className="text-sm font-medium text-gray-600">Avg. Bitrate</p>
                                        <p className="text-lg font-bold text-blue-600">{analyticsData.technical.averageBitrate}</p>
                                    </div>
                                    <div className="bg-white p-6 rounded-lg shadow-sm border">
                                        <p className="text-sm font-medium text-gray-600">Uptime</p>
                                        <p className="text-lg font-bold text-green-600">{analyticsData.technical.uptime}</p>
                                    </div>
                                    <div className="bg-white p-6 rounded-lg shadow-sm border">
                                        <p className="text-sm font-medium text-gray-600">Buffering Issues</p>
                                        <p className="text-lg font-bold text-orange-600">{analyticsData.technical.buffering}</p>
                                    </div>
                                </div>
                            </div>

                            {/* Demographics */}
                            <div>
                                <h3 className="text-lg font-semibold text-gray-900 mb-4">Audience Demographics</h3>
                                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                                    {/* Top Cities */}
                                    <div className="bg-white p-6 rounded-lg shadow-sm border">
                                        <h4 className="font-semibold text-gray-900 mb-3">Top Cities</h4>
                                        <div className="space-y-2">
                                            {analyticsData.demographics.topCities.map((city, index) => (
                                                <div key={index} className="flex justify-between">
                                                    <span className="text-sm text-gray-600">{city}</span>
                                                    <span className="text-sm font-medium">{Math.floor(Math.random() * 100)}%</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                    
                                    {/* Age Groups */}
                                    <div className="bg-white p-6 rounded-lg shadow-sm border">
                                        <h4 className="font-semibold text-gray-900 mb-3">Age Distribution</h4>
                                        <div className="space-y-2">
                                            {Object.entries(analyticsData.demographics.ageGroups).map(([ageGroup, percentage]) => (
                                                <div key={ageGroup} className="flex justify-between">
                                                    <span className="text-sm text-gray-600">{ageGroup}</span>
                                                    <span className="text-sm font-medium">{percentage}%</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                    
                                    {/* Device Types */}
                                    <div className="bg-white p-6 rounded-lg shadow-sm border">
                                        <h4 className="font-semibold text-gray-900 mb-3">Device Usage</h4>
                                        <div className="space-y-2">
                                            {Object.entries(analyticsData.demographics.deviceTypes).map(([device, percentage]) => (
                                                <div key={device} className="flex justify-between">
                                                    <span className="text-sm text-gray-600">{device}</span>
                                                    <span className="text-sm font-medium">{percentage}%</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Visual Analytics Charts */}
                            <div>
                                <h3 className="text-lg font-semibold text-gray-900 mb-4">Visual Analytics</h3>
                                <LiveStreamAnalyticsCharts 
                                    analyticsData={analyticsData} 
                                    event={event} 
                                />
                            </div>

                            {/* Additional Event Details */}
                            <div className="bg-gray-50 rounded-lg p-6">
                                <h3 className="text-lg font-semibold text-gray-900 mb-4">Event Summary</h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <h4 className="font-medium text-gray-900 mb-2">Event Information</h4>
                                        <div className="space-y-1 text-sm text-gray-600">
                                            <p><span className="font-medium">Host:</span> {event.host?.profile?.name}</p>
                                            <p><span className="font-medium">Category:</span> {event.category}</p>
                                            <p><span className="font-medium">Language:</span> {event.language}</p>
                                            <p><span className="font-medium">Access Type:</span> {event.accessType}</p>
                                            {event.price && <p><span className="font-medium">Price:</span> ₹{event.price}</p>}
                                        </div>
                                    </div>
                                    <div>
                                        <h4 className="font-medium text-gray-900 mb-2">Stream Timeline</h4>
                                        <div className="space-y-1 text-sm text-gray-600">
                                            <p><span className="font-medium">Created:</span> {format(new Date(event.createdAt), 'MMM dd, yyyy HH:mm')}</p>
                                            <p><span className="font-medium">Scheduled:</span> {format(new Date(event.scheduleTime), 'MMM dd, yyyy HH:mm')}</p>
                                            {event.startedAt && <p><span className="font-medium">Started:</span> {format(new Date(event.startedAt), 'MMM dd, yyyy HH:mm')}</p>}
                                            {event.endedAt && <p><span className="font-medium">Ended:</span> {format(new Date(event.endedAt), 'MMM dd, yyyy HH:mm')}</p>}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="text-center py-12">
                            <p className="text-gray-500">Unable to load analytics data</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default LiveStreams;
